import { Group, Stack, Text } from "@mantine/core"
import { ProgressBox, Select } from "@tfg/ui"
import _ from "lodash"
import { useMemo, useState } from "react"
import { Trans, useTranslation } from "react-i18next"

import { LibraryResponses } from "@tfg/types/endpoints/library"

import { traceabilityRequestResponseDtoStatusEnum } from "~/gen/api"
import { useDashboardStats } from "~/pages/Brand/Home/hooks/useDashboardStats"
import { useFilteredData } from "~/pages/Brand/Home/hooks/useFilteredData"
import {
  IngestionDateFilter,
  useIngestionDateFilter,
} from "~/pages/Brand/Home/hooks/useIngestionDateFilter"

const ACTIVITY_TIER_OPTIONS = [
  {
    label: "Tier 1",
    value: "1",
  },
  {
    label: "Tier 2",
    value: "2",
  },
  {
    label: "Tier 3",
    value: "3",
  },
  {
    label: "Tier 4",
    value: "4",
  },
]

interface Props {
  informationRequests: LibraryResponses.InformationRequestDashboard[]
}

export const InformationRequests = ({ informationRequests }: Props) => {
  const { t } = useTranslation("home")

  const {
    options,
    ingestionDateFilter,
    setIngestionDateFilter,
    satisfiesIngestionDateFilter,
  } = useIngestionDateFilter()

  const [brandTypeFilter, setBrandTypeFilter] = useState<string | null>(null)
  const [collection, setCollection] = useState<string | null>(null)
  const [activityTier, setActivityTier] = useState<string | null>(null)

  const filteredRequests = useFilteredData({
    data: informationRequests,
    filters: [
      (request) =>
        collection ? request.product.collection === collection : true,
      (request) =>
        brandTypeFilter ? request.product.brandType === brandTypeFilter : true,
      (request) => {
        if (!activityTier) {
          return true
        }

        return request.activityTiers.includes(parseInt(activityTier))
      },
      (request) => satisfiesIngestionDateFilter(request.product.ingestionDate),
    ],
  })

  const stats = useDashboardStats(
    filteredRequests,
    (request) =>
      request.status === traceabilityRequestResponseDtoStatusEnum.CLOSED
  )

  const averageResponseTime = useMemo(
    () =>
      Math.round(
        _.mean(
          filteredRequests
            .filter((ir) => ir.responseTime)
            .map((ir) => ir.responseTime)
        ) /
          (1000 * 3600 * 24)
      ),
    [filteredRequests]
  )

  const collections = useMemo(() => {
    return [
      ...new Set<string>(
        informationRequests
          .filter((request) => request.product.collection)
          .map((request): string => {
            return request.product.collection
          })
      ),
    ].map((collection) => {
      return {
        value: collection,
        label: collection,
      }
    })
  }, [informationRequests])

  const brandTypeOptions = useMemo(() => {
    return [
      ...new Set(
        informationRequests
          .map((request) => request.product.brandType)
          .filter((brandType) => brandType !== null)
      ),
    ].sort()
  }, [informationRequests])

  return (
    <Stack>
      <Group wrap="nowrap">
        <Select
          clearable
          size="xs"
          data={collections}
          value={collection}
          onChange={setCollection}
          placeholder={t("informationRequests.collection")}
        />
        <Select
          clearable
          size="xs"
          data={ACTIVITY_TIER_OPTIONS}
          value={activityTier}
          onChange={setActivityTier}
          placeholder={t("informationRequests.activityTier")}
        />
        <Select
          clearable
          size="xs"
          data={brandTypeOptions}
          value={brandTypeFilter}
          onChange={setBrandTypeFilter}
          placeholder={t("informationRequests.type")}
        />
        <Select
          clearable
          size="xs"
          data={options}
          value={ingestionDateFilter}
          onChange={(value) => {
            setIngestionDateFilter(value as IngestionDateFilter)
          }}
          placeholder={t("informationRequests.ingestionDate")}
        />
      </Group>
      {stats.total ? (
        <Stack bg="var(--mantine-color-white)" px="xs" py="md">
          <ProgressBox
            label={t("informationRequests.label")}
            sections={[
              {
                color: "var(--progress-bar-color-filled)",
                textColor: "var(--progress-bar-filled-text)",
                value: stats.completedPercent,
                text: stats.completedCount.toString(),
                label: t("informationRequests.closed"),
              },
              {
                color: "var(--progress-bar-color-unfilled)",
                textColor: "var(--progress-bar-unfilled-text)",
                value: stats.notCompletedPercent,
                text: stats.notCompletedCount.toString(),
                label: t("informationRequests.open"),
              },
            ]}
            helpHoverText={<Text>{t("informationRequests.totalText")}</Text>}
          />
          <Stack gap="0">
            <Text fz="sm">
              <Trans
                ns="home"
                i18nKey="informationRequests.total"
                values={{
                  total: stats.total,
                }}
                components={{
                  strong1: (
                    <strong
                      style={{
                        color: "var(--progress-bar-color-filled)",
                      }}
                    />
                  ),
                }}
              />
            </Text>
            {!isNaN(stats.completedPercent) && stats.completedPercent > 0 ? (
              <Text fz="sm">
                <Trans
                  ns="home"
                  i18nKey="informationRequests.complete"
                  values={{
                    percent: stats.completedPercent,
                  }}
                  components={{
                    strong1: (
                      <strong
                        style={{
                          color: "var(--progress-bar-color-filled)",
                        }}
                      />
                    ),
                  }}
                />
              </Text>
            ) : null}
            {isNaN(averageResponseTime) ? (
              t("informationRequests.noAverage")
            ) : (
              <Text fz="sm">
                <Trans
                  ns="home"
                  i18nKey="informationRequests.average"
                  values={{
                    days: averageResponseTime,
                  }}
                  components={{
                    strong1: (
                      <strong
                        style={{
                          color: "var(--progress-bar-color-filled)",
                        }}
                      />
                    ),
                  }}
                />
              </Text>
            )}
          </Stack>
        </Stack>
      ) : (
        <Text fz="sm">
          <strong>{t("informationRequests.noRequests")}</strong>
        </Text>
      )}
    </Stack>
  )
}
